import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Search,
  Eye,
  EyeOff,
  Pencil,
  Truck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ShieldAlert,
  Clock,
  Activity,
  Wallet,
  TrendingUp,
  Building2,
  ListChecks,
  User,
  Save,
  Phone,
  ChevronRight,
} from 'lucide-react';

const API_URL = '/api';

// ─── Helpers visuais ────────────────────────────────────────────────────────

function IndicadorCard({ label, value, icon: Icon, accent = 'brand' }) {
  const accentMap = {
    brand: 'bg-brand-50 text-brand-700',
    success: 'bg-success-50 text-success-700',
    warning: 'bg-warning-50 text-warning-700',
    info: 'bg-info-50 text-info-700',
    neutral: 'bg-surface-subtle text-ink-muted',
  };
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${accentMap[accent]}`}>
        <Icon size={18} strokeWidth={2.25} />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-lg font-semibold text-ink leading-none truncate">{value}</span>
        <span className="text-[11px] text-ink-subtle mt-1 truncate">{label}</span>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    aberto: { label: 'Aberto', cls: 'chip-warning' },
    em_andamento: { label: 'Em andamento', cls: 'chip-info' },
    concluido: { label: 'Concluído', cls: 'chip-success' },
    cancelado: { label: 'Cancelado', cls: 'chip-danger' },
  };
  const m = map[status] || { label: status || '—', cls: 'chip-neutral' };
  return <span className={m.cls}>{m.label}</span>;
}

function PrioridadeChip({ prioridade }) {
  if (prioridade === 'alta') return <span className="chip-danger">Alta</span>;
  if (prioridade === 'media') return <span className="chip-warning">Média</span>;
  if (prioridade === 'baixa') return <span className="chip-neutral">Baixa</span>;
  return <span className="chip-neutral">{prioridade || '—'}</span>;
}

function TabBtn({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-brand-600 text-white shadow-sm'
          : 'text-ink-muted hover:text-ink hover:bg-surface-subtle',
      ].join(' ')}
    >
      <span>{label}</span>
      <span className={active
        ? 'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] bg-white/25 text-white'
        : 'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] bg-surface-subtle text-ink-subtle'}>
        {count}
      </span>
    </button>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="rounded-xl bg-surface-muted p-3">
      <span className="text-[11px] uppercase tracking-wider text-ink-faint">{label}</span>
      <div className="text-sm text-ink font-medium mt-0.5 truncate">{value || '—'}</div>
    </div>
  );
}

function TextBlock({ label, children }) {
  return (
    <div>
      <span className="text-[11px] uppercase tracking-wider text-ink-faint">{label}</span>
      <div className="text-sm text-ink mt-1 whitespace-pre-wrap rounded-xl bg-surface-muted p-3 border border-line/40">
        {children}
      </div>
    </div>
  );
}

function CampoLabel({ label, children, fullWidth }) {
  return (
    <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

// ─── Component principal ────────────────────────────────────────────────────

export default function Historico({ apiFetch, podeVerRelatorios }) {
  const [atendimentos, setAtendimentos] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [erroHistorico, setErroHistorico] = useState(null);
  const [mensagemHistorico, setMensagemHistorico] = useState(null);
  const [buscaTexto, setBuscaTexto] = useState('');
  const [abaStatus, setAbaStatus] = useState('aberto');

  const [prestadores, setPrestadores] = useState([]);
  const [carregandoPrestadores, setCarregandoPrestadores] = useState(false);
  const [atendimentoEmAcaoId, setAtendimentoEmAcaoId] = useState(null);
  const [prestadorSelecionadoId, setPrestadorSelecionadoId] = useState('');
  const [valorNegociado, setValorNegociado] = useState('');
  const [observacoesAcionamento, setObservacoesAcionamento] = useState('');
  const [buscaPrestador, setBuscaPrestador] = useState('');
  const [regiaoPrestador, setRegiaoPrestador] = useState('');
  const [tipoPrestador, setTipoPrestador] = useState('');
  const [salvandoAcionamento, setSalvandoAcionamento] = useState(false);
  const [processandoStatusId, setProcessandoStatusId] = useState(null);
  const [detalhesAbertos, setDetalhesAbertos] = useState({});

  const [atendimentoEmEdicaoId, setAtendimentoEmEdicaoId] = useState(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [formEdicao, setFormEdicao] = useState({
    tipoAtendimento: 'colisao',
    prioridade: 'media',
    localizacao: '',
    placaAtendimento: '',
    modeloVeiculoAtendimento: '',
    nomeComunicante: '',
    contato: '',
    comunicanteCondutor: 'Sim',
    cnhEmPosse: 'Sim',
    crlvEmPosse: 'Sim',
    horarioFato: '',
    passageiros: '1',
    idososCriancasPcd: 'Não',
    envolveuOutrosVeiculos: '0',
    quemCausou: 'Terceiro',
    consumoAlcoolMedicamentos: 'Não',
    rodasTravadas: 'Não',
    cargaSobrepeso: 'Não',
    problemaRelato: '',
    pendencias: '',
  });

  const carregarHistorico = async () => {
    try {
      setCarregandoHistorico(true);
      setErroHistorico(null);
      const response = await apiFetch(`${API_URL}/atendimentos`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao carregar atendimentos');
      setAtendimentos(Array.isArray(data) ? data : []);
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao carregar atendimentos');
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const carregarPrestadores = async () => {
    try {
      setCarregandoPrestadores(true);
      setErroHistorico(null);

      const params = new URLSearchParams();
      if (buscaPrestador.trim()) params.set('busca', buscaPrestador.trim());
      if (regiaoPrestador.trim()) params.set('regiao', regiaoPrestador.trim());
      if (tipoPrestador.trim()) params.set('tipo', tipoPrestador.trim());

      const query = params.toString();
      const response = await apiFetch(`${API_URL}/prestadores${query ? `?${query}` : ''}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao carregar prestadores');
      setPrestadores(Array.isArray(data) ? data : []);
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao carregar prestadores');
    } finally {
      setCarregandoPrestadores(false);
    }
  };

  const abrirPainelAcionamento = async (atendimentoId) => {
    const jaAberto = atendimentoEmAcaoId === atendimentoId;
    if (jaAberto) {
      setAtendimentoEmAcaoId(null);
      return;
    }
    setAtendimentoEmEdicaoId(null);
    setAtendimentoEmAcaoId(atendimentoId);
    setPrestadorSelecionadoId('');
    setValorNegociado('');
    setObservacoesAcionamento('');
    setBuscaPrestador('');
    setRegiaoPrestador('');
    setTipoPrestador('');
    setMensagemHistorico(null);
    try { await carregarPrestadores(); } catch { /* erro já tratado */ }
  };

  const abrirPainelEdicao = (item) => {
    const jaAberto = atendimentoEmEdicaoId === item.id;
    if (jaAberto) {
      setAtendimentoEmEdicaoId(null);
      return;
    }
    const dc = item.dadosComplementares || {};
    setAtendimentoEmAcaoId(null);
    setAtendimentoEmEdicaoId(item.id);
    setMensagemHistorico(null);
    setErroHistorico(null);
    setFormEdicao({
      tipoAtendimento: item.tipoAtendimento || 'geral',
      prioridade: item.prioridade || 'media',
      localizacao: item.localizacao || '',
      placaAtendimento: item.placaAtendimento || '',
      modeloVeiculoAtendimento: item.modeloVeiculoAtendimento || '',
      nomeComunicante: dc.nomeComunicante || '',
      contato: dc.contato || '',
      comunicanteCondutor: dc.comunicanteCondutor || 'Sim',
      cnhEmPosse: dc.cnhEmPosse || 'Sim',
      crlvEmPosse: dc.crlvEmPosse || 'Sim',
      horarioFato: dc.horarioFato || '',
      passageiros: String(dc.passageiros ?? '1'),
      idososCriancasPcd: dc.idososCriancasPcd || 'Não',
      envolveuOutrosVeiculos: String(dc.envolveuOutrosVeiculos ?? '0'),
      quemCausou: dc.quemCausou || 'Terceiro',
      consumoAlcoolMedicamentos: dc.consumoAlcoolMedicamentos || 'Não',
      rodasTravadas: dc.rodasTravadas || 'Não',
      cargaSobrepeso: dc.cargaSobrepeso || 'Não',
      problemaRelato: item.problemaRelato || '',
      pendencias: dc.pendencias || '',
    });
  };

  const atualizarCampoEdicao = (campo, valor) => {
    setFormEdicao((prev) => ({ ...prev, [campo]: valor }));
  };

  const salvarEdicao = async (id) => {
    try {
      setSalvandoEdicao(true);
      setErroHistorico(null);
      setMensagemHistorico(null);
      if (!formEdicao.localizacao.trim()) throw new Error('Informe a localização.');
      if (!formEdicao.problemaRelato.trim()) throw new Error('Informe o relato do atendimento.');

      const payload = {
        localizacao: formEdicao.localizacao.trim(),
        problemaRelato: formEdicao.problemaRelato.trim(),
        prioridade: formEdicao.prioridade,
        tipoAtendimento: formEdicao.tipoAtendimento,
        placaAtendimento: formEdicao.placaAtendimento,
        modeloVeiculoAtendimento: formEdicao.modeloVeiculoAtendimento,
        dadosComplementares: {
          nomeComunicante: formEdicao.nomeComunicante,
          contato: formEdicao.contato,
          comunicanteCondutor: formEdicao.comunicanteCondutor,
          cnhEmPosse: formEdicao.cnhEmPosse,
          crlvEmPosse: formEdicao.crlvEmPosse,
          horarioFato: formEdicao.horarioFato,
          passageiros: Number(formEdicao.passageiros || 0),
          idososCriancasPcd: formEdicao.idososCriancasPcd,
          envolveuOutrosVeiculos: Number(formEdicao.envolveuOutrosVeiculos || 0),
          quemCausou: formEdicao.quemCausou,
          consumoAlcoolMedicamentos: formEdicao.consumoAlcoolMedicamentos,
          rodasTravadas: formEdicao.rodasTravadas,
          cargaSobrepeso: formEdicao.cargaSobrepeso,
          pendencias: formEdicao.pendencias,
        },
      };

      const response = await apiFetch(`${API_URL}/atendimentos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao editar atendimento');
      setMensagemHistorico('Atendimento atualizado com sucesso.');
      setAtendimentoEmEdicaoId(null);
      await carregarHistorico();
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao editar atendimento');
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const confirmarAcionamento = async (atendimentoId) => {
    try {
      setSalvandoAcionamento(true);
      setErroHistorico(null);
      setMensagemHistorico(null);
      if (!prestadorSelecionadoId) throw new Error('Selecione um prestador.');

      const payload = {
        atendimentoId,
        prestadorId: prestadorSelecionadoId,
        valorNegociado: valorNegociado ? Number(valorNegociado) : undefined,
        observacoes: observacoesAcionamento || undefined,
      };

      const response = await apiFetch(`${API_URL}/acionamentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao acionar prestador');

      setMensagemHistorico('Prestador acionado com sucesso.');
      setAtendimentoEmAcaoId(null);
      setPrestadorSelecionadoId('');
      setValorNegociado('');
      setObservacoesAcionamento('');
      await carregarHistorico();
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao acionar prestador');
    } finally {
      setSalvandoAcionamento(false);
    }
  };

  const atualizarStatusAtendimento = async (atendimentoId, status) => {
    const response = await apiFetch(`${API_URL}/atendimentos/${atendimentoId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erro ao atualizar status do atendimento');
    return data;
  };

  const atualizarStatusAcionamento = async (acionamentoId, status) => {
    const response = await apiFetch(`${API_URL}/acionamentos/${acionamentoId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erro ao atualizar status do acionamento');
    return data;
  };

  const concluirAtendimento = async (item) => {
    try {
      setProcessandoStatusId(item.id);
      setErroHistorico(null);
      setMensagemHistorico(null);
      const primeiroAcionamento = item.acionamentos?.[0] || null;
      if (primeiroAcionamento) {
        await atualizarStatusAcionamento(primeiroAcionamento.id, 'concluido');
      }
      await atualizarStatusAtendimento(item.id, 'concluido');
      setMensagemHistorico('Atendimento concluído com sucesso.');
      await carregarHistorico();
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao concluir atendimento');
    } finally {
      setProcessandoStatusId(null);
    }
  };

  const cancelarAtendimento = async (item) => {
    try {
      setProcessandoStatusId(item.id);
      setErroHistorico(null);
      setMensagemHistorico(null);
      const primeiroAcionamento = item.acionamentos?.[0] || null;
      if (primeiroAcionamento) {
        await atualizarStatusAcionamento(primeiroAcionamento.id, 'cancelado');
      }
      await atualizarStatusAtendimento(item.id, 'cancelado');
      setMensagemHistorico('Atendimento cancelado com sucesso.');
      await carregarHistorico();
    } catch (err) {
      setErroHistorico(err.message || 'Erro ao cancelar atendimento');
    } finally {
      setProcessandoStatusId(null);
    }
  };

  const alternarDetalhes = (id) => {
    setDetalhesAbertos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => { carregarHistorico(); }, []);

  const totais = useMemo(() => ({
    aberto: atendimentos.filter((i) => i.status === 'aberto').length,
    em_andamento: atendimentos.filter((i) => i.status === 'em_andamento').length,
    concluido: atendimentos.filter((i) => i.status === 'concluido').length,
    cancelado: atendimentos.filter((i) => i.status === 'cancelado').length,
    todos: atendimentos.length,
  }), [atendimentos]);

  const indicadores = useMemo(() => {
    const comPrestador = atendimentos.filter((i) => (i.acionamentos?.length || 0) > 0).length;
    const semPrestador = atendimentos.filter((i) => (i.acionamentos?.length || 0) === 0).length;
    const valores = atendimentos
      .flatMap((i) => i.acionamentos || [])
      .filter((a) => a.valorNegociado != null)
      .map((a) => Number(a.valorNegociado));
    const valorTotal = valores.reduce((acc, v) => acc + v, 0);
    const ticketMedio = valores.length > 0 ? valorTotal / valores.length : 0;
    return { comPrestador, semPrestador, valorTotal, ticketMedio };
  }, [atendimentos]);

  const atendimentosFiltrados = useMemo(() => {
    const termo = buscaTexto.trim().toLowerCase();
    return atendimentos.filter((item) => {
      const bateStatus = abaStatus === 'todos' ? true : item.status === abaStatus;
      const nomeAssociado = item.associado?.nome?.toLowerCase() || '';
      const localizacao = item.localizacao?.toLowerCase() || '';
      const problema = item.problemaRelato?.toLowerCase() || '';
      const nomeUsuario = item.usuario?.nome?.toLowerCase() || '';
      const tipoAtendimento = item.tipoAtendimento?.toLowerCase?.() || '';
      const placaAtendimento = item.placaAtendimento?.toLowerCase?.() || '';
      const nomePrestador = item.acionamentos
        ?.map((a) => a.prestador?.razaoSocial || '')
        .join(' ').toLowerCase() || '';

      const bateBusca = !termo ||
        nomeAssociado.includes(termo) ||
        localizacao.includes(termo) ||
        problema.includes(termo) ||
        nomeUsuario.includes(termo) ||
        tipoAtendimento.includes(termo) ||
        placaAtendimento.includes(termo) ||
        nomePrestador.includes(termo);

      return bateStatus && bateBusca;
    });
  }, [atendimentos, abaStatus, buscaTexto]);

  const formatarStatus = (status) => {
    if (status === 'aberto') return 'Aberto';
    if (status === 'em_andamento') return 'Em andamento';
    if (status === 'concluido') return 'Concluído';
    if (status === 'cancelado') return 'Cancelado';
    return status || '—';
  };

  const formatarTipo = (tipo) => {
    if (!tipo) return 'Geral';
    if (tipo === 'roubo_furto') return 'Roubo/Furto';
    return tipo.charAt(0).toUpperCase() + tipo.slice(1);
  };

  const mostrarValor = (valor) => {
    if (valor == null) return '—';
    return `R$ ${Number(valor).toFixed(2)}`;
  };

  const formatarDataHoraLinhaTempo = (data) => {
    if (!data) return 'Status atual';
    return new Date(data).toLocaleString('pt-BR');
  };

  const montarLinhaTempo = (item) => {
    const eventos = [];
    eventos.push({
      titulo: 'Atendimento aberto',
      descricao: `Chamado registrado por ${item.usuario?.nome || 'usuário não informado'}`,
      data: item.dataHoraChamado,
      tipo: 'open',
    });
    const primeiroAcionamento = item.acionamentos?.[0] || null;
    if (primeiroAcionamento) {
      eventos.push({
        titulo: 'Prestador acionado',
        descricao: `${primeiroAcionamento.prestador?.razaoSocial || 'Prestador não informado'}${primeiroAcionamento.valorNegociado != null ? ` • ${mostrarValor(primeiroAcionamento.valorNegociado)}` : ''}`,
        data: primeiroAcionamento.dataHoraAcionamento || primeiroAcionamento.createdAt || null,
        tipo: 'dispatch',
      });
    }
    eventos.push({
      titulo: `Status atual: ${formatarStatus(item.status)}`,
      descricao: 'Situação atual do atendimento',
      data: null,
      tipo: item.status === 'concluido' ? 'success' : item.status === 'cancelado' ? 'danger' : 'current',
    });
    return eventos;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Central de Atendimentos</h1>
          <p className="text-sm text-ink-subtle mt-1">
            Acompanhe os atendimentos em aberto, em andamento e concluídos.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={carregarHistorico}
          disabled={carregandoHistorico}
        >
          {carregandoHistorico ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {carregandoHistorico ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {!podeVerRelatorios && (
        <div className="flex items-start gap-2 rounded-xl bg-warning-50 text-warning-700 px-3.5 py-2.5 text-sm">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          <span>Seu perfil não tem permissão para acessar relatórios.</span>
        </div>
      )}

      {erroHistorico && (
        <div className="flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erroHistorico}</span>
        </div>
      )}
      {mensagemHistorico && (
        <div className="flex items-start gap-2 rounded-xl bg-success-50 text-success-700 px-3.5 py-2.5 text-sm">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{mensagemHistorico}</span>
        </div>
      )}

      {/* Indicadores */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <IndicadorCard label="Total" value={totais.todos} icon={ListChecks} accent="brand" />
        <IndicadorCard label="Abertos" value={totais.aberto} icon={Activity} accent="warning" />
        <IndicadorCard label="Em andamento" value={totais.em_andamento} icon={Clock} accent="info" />
        <IndicadorCard label="Com prestador" value={indicadores.comPrestador} icon={Truck} accent="success" />
        <IndicadorCard label="Sem prestador" value={indicadores.semPrestador} icon={Truck} accent="neutral" />
        <IndicadorCard label="Valor total" value={`R$ ${indicadores.valorTotal.toFixed(2)}`} icon={Wallet} accent="brand" />
        <IndicadorCard label="Ticket médio" value={`R$ ${indicadores.ticketMedio.toFixed(2)}`} icon={TrendingUp} accent="info" />
      </div>

      {/* Tabs + busca */}
      <section className="card p-4 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <TabBtn active={abaStatus === 'aberto'} label="Abertos" count={totais.aberto} onClick={() => setAbaStatus('aberto')} />
          <TabBtn active={abaStatus === 'em_andamento'} label="Em andamento" count={totais.em_andamento} onClick={() => setAbaStatus('em_andamento')} />
          <TabBtn active={abaStatus === 'concluido'} label="Concluídos" count={totais.concluido} onClick={() => setAbaStatus('concluido')} />
          <TabBtn active={abaStatus === 'cancelado'} label="Cancelados" count={totais.cancelado} onClick={() => setAbaStatus('cancelado')} />
          <TabBtn active={abaStatus === 'todos'} label="Todos" count={totais.todos} onClick={() => setAbaStatus('todos')} />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por associado, local, problema, tipo, placa ou prestador"
              value={buscaTexto}
              onChange={(e) => setBuscaTexto(e.target.value)}
              className="input pl-9"
            />
          </div>
          <span className="text-xs text-ink-subtle whitespace-nowrap">
            {atendimentosFiltrados.length} encontrado(s)
          </span>
        </div>
      </section>

      {/* Lista */}
      {carregandoHistorico ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="h-4 bg-surface-subtle rounded w-1/3 mb-3" />
              <div className="h-3 bg-surface-subtle rounded w-1/2 mb-5" />
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="h-12 bg-surface-subtle rounded" />
                <div className="h-12 bg-surface-subtle rounded" />
                <div className="h-12 bg-surface-subtle rounded" />
              </div>
              <div className="h-10 bg-surface-subtle rounded" />
            </div>
          ))}
        </div>
      ) : atendimentosFiltrados.length === 0 ? (
        <div className="card p-8 flex flex-col items-center text-center">
          <ListChecks size={28} className="text-ink-faint mb-2" />
          <p className="text-sm text-ink-muted">Nenhum atendimento encontrado.</p>
          <p className="text-xs text-ink-faint mt-1">Ajuste os filtros ou a aba de status.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {atendimentosFiltrados.map((item) => {
            const primeiroAcionamento = item.acionamentos?.[0] || null;
            const dadosComplementares = item.dadosComplementares || {};
            const podeAcionar = item.status === 'aberto' && !primeiroAcionamento;
            const podeCancelar = item.status === 'aberto' || item.status === 'em_andamento';
            const podeConcluir = item.status === 'em_andamento';
            const detalhesAbertosItem = !!detalhesAbertos[item.id];
            const editandoItem = atendimentoEmEdicaoId === item.id;
            const linhaTempo = montarLinhaTempo(item);
            const processandoStatus = processandoStatusId === item.id;

            return (
              <article key={item.id} className="card p-5 flex flex-col gap-4">
                {/* Header do card */}
                <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-ink truncate">
                      {item.associado?.nome || 'Associado não informado'}
                    </h3>
                    <p className="text-xs text-ink-subtle mt-0.5 truncate">
                      {formatarTipo(item.tipoAtendimento)} • {item.localizacao || 'Local não informado'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <PrioridadeChip prioridade={item.prioridade} />
                    <StatusChip status={item.status} />
                  </div>
                </header>

                {/* Grid de informações */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <InfoCell label="Problema relatado" value={item.problemaRelato} />
                  <InfoCell label="Atendente" value={item.usuario?.nome} />
                  <InfoCell
                    label="Data do chamado"
                    value={item.dataHoraChamado ? new Date(item.dataHoraChamado).toLocaleString('pt-BR') : null}
                  />
                  <InfoCell
                    label="Veículo"
                    value={`${item.placaAtendimento || '—'}${item.modeloVeiculoAtendimento ? ` • ${item.modeloVeiculoAtendimento}` : ''}`}
                  />
                  <InfoCell label="Comunicante" value={dadosComplementares.nomeComunicante} />
                  <InfoCell label="Contato" value={dadosComplementares.contato} />
                </div>

                {/* Barra de operação */}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-line/60">
                  {primeiroAcionamento ? (
                    <>
                      <span className="chip-info">
                        <Truck size={12} /> {primeiroAcionamento.prestador?.razaoSocial || 'Não informado'}
                      </span>
                      <span className="chip-brand">
                        <Wallet size={12} /> {mostrarValor(primeiroAcionamento.valorNegociado)}
                      </span>
                    </>
                  ) : (
                    <span className="chip-warning">
                      <Clock size={12} /> Aguardando acionamento de prestador
                    </span>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => alternarDetalhes(item.id)}
                  >
                    {detalhesAbertosItem ? <EyeOff size={16} /> : <Eye size={16} />}
                    {detalhesAbertosItem ? 'Ocultar detalhes' : 'Ver detalhes'}
                  </button>

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => abrirPainelEdicao(item)}
                  >
                    <Pencil size={16} />
                    {editandoItem ? 'Fechar edição' : 'Editar'}
                  </button>

                  <div className="flex-1" />

                  {podeAcionar && (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => abrirPainelAcionamento(item.id)}
                    >
                      <Truck size={16} />
                      {atendimentoEmAcaoId === item.id ? 'Fechar acionamento' : 'Acionar prestador'}
                    </button>
                  )}

                  {podeConcluir && (
                    <button
                      type="button"
                      className="btn-primary !bg-success-600 hover:!bg-success-700"
                      onClick={() => concluirAtendimento(item)}
                      disabled={processandoStatus}
                    >
                      {processandoStatus ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      {processandoStatus ? 'Processando…' : 'Concluir'}
                    </button>
                  )}

                  {podeCancelar && (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => cancelarAtendimento(item)}
                      disabled={processandoStatus}
                    >
                      {processandoStatus ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                      {processandoStatus ? 'Processando…' : 'Cancelar'}
                    </button>
                  )}
                </div>

                {/* Painel de detalhes */}
                {detalhesAbertosItem && (
                  <div className="rounded-2xl bg-surface-muted/60 border border-line/40 p-5 flex flex-col gap-5">
                    <h4 className="text-sm font-semibold text-ink flex items-center gap-2">
                      <Eye size={14} className="text-ink-muted" /> Detalhes do atendimento
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <InfoCell label="Associado" value={item.associado?.nome} />
                      <InfoCell label="CPF" value={item.associado?.cpf} />
                      <InfoCell label="Tipo" value={formatarTipo(item.tipoAtendimento)} />
                      <InfoCell label="Prioridade" value={item.prioridade} />
                      <InfoCell label="Horário do fato" value={dadosComplementares.horarioFato} />
                      <InfoCell label="Local do fato" value={item.localizacao} />
                      <InfoCell label="Comunicante é o condutor" value={dadosComplementares.comunicanteCondutor} />
                      <InfoCell label="CNH em posse" value={dadosComplementares.cnhEmPosse} />
                      <InfoCell label="CRLV em posse" value={dadosComplementares.crlvEmPosse} />
                      <InfoCell label="Passageiros" value={dadosComplementares.passageiros} />
                      <InfoCell label="Idosos/criança/PCD" value={dadosComplementares.idososCriancasPcd} />
                      <InfoCell label="Outros veículos envolvidos" value={dadosComplementares.envolveuOutrosVeiculos} />
                      <InfoCell label="Quem causou" value={dadosComplementares.quemCausou} />
                      <InfoCell label="Álcool/medicamentos" value={dadosComplementares.consumoAlcoolMedicamentos} />
                      <InfoCell label="Rodas travadas" value={dadosComplementares.rodasTravadas} />
                      <InfoCell label="Carga/sobrepeso" value={dadosComplementares.cargaSobrepeso} />
                    </div>

                    <TextBlock label="Relato do atendimento">
                      {item.problemaRelato || '—'}
                    </TextBlock>
                    <TextBlock label="Pendências">
                      {dadosComplementares.pendencias || '—'}
                    </TextBlock>
                    <TextBlock label="Prestador acionado">
                      {primeiroAcionamento
                        ? `${primeiroAcionamento.prestador?.razaoSocial || 'Não informado'} • ${mostrarValor(primeiroAcionamento.valorNegociado)}`
                        : 'Nenhum prestador acionado'}
                    </TextBlock>
                    {primeiroAcionamento?.observacoes && (
                      <TextBlock label="Observações do acionamento">
                        {primeiroAcionamento.observacoes}
                      </TextBlock>
                    )}

                    {/* Linha do tempo */}
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-ink-faint">Linha do tempo</span>
                      <ol className="mt-3 relative border-l-2 border-line ml-2 pl-5 flex flex-col gap-4">
                        {linhaTempo.map((evento, i) => {
                          const dotColor = {
                            open: 'bg-brand-500',
                            dispatch: 'bg-info-500',
                            success: 'bg-success-500',
                            danger: 'bg-danger-500',
                            current: 'bg-warning-500',
                          }[evento.tipo] || 'bg-ink-faint';
                          return (
                            <li key={i} className="relative">
                              <span className={`absolute -left-[27px] top-1.5 w-3 h-3 rounded-full ring-4 ring-surface-muted ${dotColor}`} />
                              <div className="flex flex-col">
                                <strong className="text-sm text-ink">{evento.titulo}</strong>
                                <span className="text-xs text-ink-subtle mt-0.5">{formatarDataHoraLinhaTempo(evento.data)}</span>
                                <span className="text-xs text-ink-muted mt-1">{evento.descricao}</span>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  </div>
                )}

                {/* Painel de edição */}
                {editandoItem && (
                  <div className="rounded-2xl bg-surface-muted/60 border border-line/40 p-5">
                    <h4 className="text-sm font-semibold text-ink flex items-center gap-2 mb-4">
                      <Pencil size={14} className="text-ink-muted" /> Editar atendimento
                    </h4>

                    <form
                      onSubmit={(e) => { e.preventDefault(); salvarEdicao(item.id); }}
                      className="flex flex-col gap-5"
                    >
                      <fieldset className="border border-line/60 rounded-2xl p-5 bg-surface">
                        <legend className="px-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">Classificação</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          <CampoLabel label="Tipo">
                            <select value={formEdicao.tipoAtendimento} onChange={(e) => atualizarCampoEdicao('tipoAtendimento', e.target.value)} className="input">
                              <option value="colisao">Colisão</option>
                              <option value="pane">Pane</option>
                              <option value="reboque">Reboque</option>
                              <option value="roubo_furto">Roubo/Furto</option>
                              <option value="outro">Outro</option>
                              <option value="geral">Geral</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Prioridade">
                            <select value={formEdicao.prioridade} onChange={(e) => atualizarCampoEdicao('prioridade', e.target.value)} className="input">
                              <option value="baixa">Baixa</option>
                              <option value="media">Média</option>
                              <option value="alta">Alta</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Horário do fato">
                            <input type="text" value={formEdicao.horarioFato} onChange={(e) => atualizarCampoEdicao('horarioFato', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Localização *">
                            <input type="text" value={formEdicao.localizacao} onChange={(e) => atualizarCampoEdicao('localizacao', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Placa">
                            <input type="text" value={formEdicao.placaAtendimento} onChange={(e) => atualizarCampoEdicao('placaAtendimento', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Modelo">
                            <input type="text" value={formEdicao.modeloVeiculoAtendimento} onChange={(e) => atualizarCampoEdicao('modeloVeiculoAtendimento', e.target.value)} className="input" />
                          </CampoLabel>
                        </div>
                      </fieldset>

                      <fieldset className="border border-line/60 rounded-2xl p-5 bg-surface">
                        <legend className="px-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">Comunicante</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          <CampoLabel label="Nome">
                            <input type="text" value={formEdicao.nomeComunicante} onChange={(e) => atualizarCampoEdicao('nomeComunicante', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Contato">
                            <input type="text" value={formEdicao.contato} onChange={(e) => atualizarCampoEdicao('contato', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="É o condutor?">
                            <select value={formEdicao.comunicanteCondutor} onChange={(e) => atualizarCampoEdicao('comunicanteCondutor', e.target.value)} className="input">
                              <option value="Sim">Sim</option>
                              <option value="Não">Não</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="CNH em posse">
                            <select value={formEdicao.cnhEmPosse} onChange={(e) => atualizarCampoEdicao('cnhEmPosse', e.target.value)} className="input">
                              <option value="Sim">Sim</option>
                              <option value="Não">Não</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="CRLV em posse">
                            <select value={formEdicao.crlvEmPosse} onChange={(e) => atualizarCampoEdicao('crlvEmPosse', e.target.value)} className="input">
                              <option value="Sim">Sim</option>
                              <option value="Não">Não</option>
                            </select>
                          </CampoLabel>
                        </div>
                      </fieldset>

                      <fieldset className="border border-line/60 rounded-2xl p-5 bg-surface">
                        <legend className="px-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">Situação do evento</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          <CampoLabel label="Passageiros">
                            <input type="number" value={formEdicao.passageiros} onChange={(e) => atualizarCampoEdicao('passageiros', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Outros veículos envolvidos">
                            <input type="number" value={formEdicao.envolveuOutrosVeiculos} onChange={(e) => atualizarCampoEdicao('envolveuOutrosVeiculos', e.target.value)} className="input" />
                          </CampoLabel>
                          <CampoLabel label="Idosos/criança/PCD">
                            <select value={formEdicao.idososCriancasPcd} onChange={(e) => atualizarCampoEdicao('idososCriancasPcd', e.target.value)} className="input">
                              <option value="Não">Não</option>
                              <option value="Sim">Sim</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Quem causou">
                            <select value={formEdicao.quemCausou} onChange={(e) => atualizarCampoEdicao('quemCausou', e.target.value)} className="input">
                              <option value="Associado">Associado</option>
                              <option value="Terceiro">Terceiro</option>
                              <option value="Não definido">Não definido</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Álcool/medicamentos">
                            <select value={formEdicao.consumoAlcoolMedicamentos} onChange={(e) => atualizarCampoEdicao('consumoAlcoolMedicamentos', e.target.value)} className="input">
                              <option value="Não">Não</option>
                              <option value="Sim">Sim</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Rodas travadas">
                            <select value={formEdicao.rodasTravadas} onChange={(e) => atualizarCampoEdicao('rodasTravadas', e.target.value)} className="input">
                              <option value="Não">Não</option>
                              <option value="Sim">Sim</option>
                            </select>
                          </CampoLabel>
                          <CampoLabel label="Carga/sobrepeso">
                            <select value={formEdicao.cargaSobrepeso} onChange={(e) => atualizarCampoEdicao('cargaSobrepeso', e.target.value)} className="input">
                              <option value="Não">Não</option>
                              <option value="Sim">Sim</option>
                            </select>
                          </CampoLabel>
                        </div>
                      </fieldset>

                      <fieldset className="border border-line/60 rounded-2xl p-5 bg-surface">
                        <legend className="px-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">Relato e pendências</legend>
                        <div className="flex flex-col gap-3">
                          <div>
                            <label className="label">Relato do atendimento *</label>
                            <textarea
                              value={formEdicao.problemaRelato}
                              onChange={(e) => atualizarCampoEdicao('problemaRelato', e.target.value)}
                              className="input min-h-[120px]"
                              rows={5}
                            />
                          </div>
                          <div>
                            <label className="label">Pendências</label>
                            <textarea
                              value={formEdicao.pendencias}
                              onChange={(e) => atualizarCampoEdicao('pendencias', e.target.value)}
                              className="input min-h-[72px]"
                              rows={3}
                            />
                          </div>
                        </div>
                      </fieldset>

                      <div className="flex justify-end">
                        <button type="submit" className="btn-primary" disabled={salvandoEdicao}>
                          {salvandoEdicao ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                          {salvandoEdicao ? 'Salvando…' : 'Salvar alterações'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Painel de acionamento */}
                {atendimentoEmAcaoId === item.id && (
                  <div className="rounded-2xl bg-surface-muted/60 border border-line/40 p-5 flex flex-col gap-4">
                    <h4 className="text-sm font-semibold text-ink flex items-center gap-2">
                      <Truck size={14} className="text-ink-muted" /> Acionar prestador
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Buscar prestador"
                          value={buscaPrestador}
                          onChange={(e) => setBuscaPrestador(e.target.value)}
                          className="input pl-9"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Região"
                        value={regiaoPrestador}
                        onChange={(e) => setRegiaoPrestador(e.target.value)}
                        className="input"
                      />
                      <input
                        type="text"
                        placeholder="Tipo de serviço"
                        value={tipoPrestador}
                        onChange={(e) => setTipoPrestador(e.target.value)}
                        className="input"
                      />
                    </div>

                    <div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={carregarPrestadores}
                        disabled={carregandoPrestadores}
                      >
                        {carregandoPrestadores ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                        {carregandoPrestadores ? 'Buscando…' : 'Buscar prestadores'}
                      </button>
                    </div>

                    {prestadores.length === 0 ? (
                      <p className="text-sm text-ink-subtle">Nenhum prestador encontrado.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {prestadores.map((p) => {
                          const selecionado = String(p.id) === String(prestadorSelecionadoId);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setPrestadorSelecionadoId(p.id)}
                              className={[
                                'rounded-xl border p-4 text-left flex flex-col gap-1.5 transition-all',
                                selecionado
                                  ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500/30'
                                  : 'border-line bg-surface hover:border-line-strong hover:shadow-soft',
                              ].join(' ')}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <strong className="text-sm text-ink truncate">{p.razaoSocial}</strong>
                                {selecionado && <CheckCircle2 size={16} className="text-brand-600 shrink-0" />}
                              </div>
                              <span className="text-xs text-ink-subtle">
                                {p.regiaoAtuacao} • {Array.isArray(p.tiposServico) ? p.tiposServico.join(', ') : '—'}
                              </span>
                              <span className="text-xs text-ink-subtle flex items-center gap-1">
                                <Phone size={11} /> {p.telefonePrincipal}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="label">Valor negociado (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={valorNegociado}
                          onChange={(e) => setValorNegociado(e.target.value)}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Observações</label>
                        <input
                          type="text"
                          value={observacoesAcionamento}
                          onChange={(e) => setObservacoesAcionamento(e.target.value)}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => confirmarAcionamento(item.id)}
                        disabled={salvandoAcionamento}
                      >
                        {salvandoAcionamento ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                        {salvandoAcionamento ? 'Confirmando…' : 'Confirmar acionamento'}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
